import { FastColor } from '@ant-design/fast-color'
import type { GlobalToken } from 'antd'

import { contrastRatio } from 'src/theme/contrast'

const TINT_FACTOR = 85
const BORDER_TINT_FACTOR = 60
const NON_TEXT_CONTRAST = 3
const LIFT_STEP = 10

type IconToken = Pick<
  GlobalToken,
  'colorBgContainer' | 'colorText' | 'colorTextSecondary'
>

const liftTowardText = (color: string, background: string, text: string) => {
  if (contrastRatio(color, background) >= NON_TEXT_CONTRAST) return color

  for (let share = LIFT_STEP; share < 100; share += LIFT_STEP) {
    const lifted = new FastColor(color).mix(text, share).toRgbString()

    if (contrastRatio(lifted, background) >= NON_TEXT_CONTRAST) return lifted
  }

  return text
}

type IconColors = {
  iconColor: string
  backgroundColor: string
  borderColor: string
}

const iconColorsCache = new Map<string, IconColors>()

export const resolveIconColors = (
  token: IconToken,
  color?: string
): IconColors => {
  const resolvedColor = color || token.colorTextSecondary
  const cacheKey = `${resolvedColor}|${token.colorBgContainer}|${token.colorText}`
  const cached = iconColorsCache.get(cacheKey)

  if (cached) return cached

  const backgroundColor = new FastColor(resolvedColor)
    .mix(token.colorBgContainer, TINT_FACTOR)
    .toRgbString()
  const borderColor = new FastColor(resolvedColor)
    .mix(token.colorBgContainer, BORDER_TINT_FACTOR)
    .toRgbString()

  const colors = {
    iconColor: liftTowardText(resolvedColor, backgroundColor, token.colorText),
    backgroundColor,
    borderColor
  }

  iconColorsCache.set(cacheKey, colors)

  return colors
}
